import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MapPin, Eye, Package, Star, ChatCircleDots, Truck, HandArrowDown,
  CaretLeft, Tag, Stack, CreditCard,
} from '@phosphor-icons/react';
import { MarketplaceCard } from '../components/MarketplaceCard';
import {
  API, CATEGORY_LABELS, CONDITION_LABELS, CONDITION_COLORS,
  DELIVERY_LABELS, photoUrl, formatPrice,
} from '../utils/marketplace';
import { setPageMeta } from '../utils/seo';
import { openCheckout } from '../utils/checkout';

export default function MarketplaceListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [buying, setBuying] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [showOffer, setShowOffer] = useState(false);
  const [myOffer, setMyOffer] = useState(null);

  const refreshOffers = React.useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/api/marketplace/offers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const mine = (res.data.sent || []).find((o) => o.listing_id === id);
        setMyOffer(mine || null);
      })
      .catch(() => {});
  }, [id]);

  useEffect(() => { refreshOffers(); }, [refreshOffers]);

  const acceptedOffer = myOffer?.status === 'accepted' ? myOffer : null;

  const sendOffer = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate(`/login?redirect=/marketplace/listing/${id}`); return; }
    try {
      await axios.post(`${API}/api/marketplace/listings/${id}/offer`,
        { amount: Number(offerAmount) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Offer sent! The seller will be notified.');
      setShowOffer(false);
      setOfferAmount('');
      refreshOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send offer');
    }
  };

  const respondCounter = async (action) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/marketplace/offers/${myOffer._id}/counter-response`,
        { action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(action === 'accept'
        ? `Counter accepted — you can now buy at ${formatPrice(myOffer.counter_amount)}!`
        : 'Counter-offer declined');
      refreshOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not respond to counter');
    }
  };

  useEffect(() => {
    setLoading(true);
    setActivePhoto(0);
    axios.get(`${API}/api/marketplace/listings/${id}`)
      .then((res) => {
        setData(res.data);
        const l = res.data.listing;
        setPageMeta({
          title: `${l.title} — ${formatPrice(l.price)} | ConstructHelp Marketplace`,
          description: `${CONDITION_LABELS[l.condition]} ${CATEGORY_LABELS[l.category]} for sale in ${l.city}, ${l.state}. ${l.description.slice(0, 140)}`,
          url: `${window.location.origin}/marketplace/listing/${l._id}`,
          ogType: 'product',
          image: l.photos?.length ? `${API}${'/api/marketplace/photo/' + l.photos[0]}` : undefined,
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: l.title,
            description: l.description.slice(0, 300),
            image: (l.photos || []).map((p) => `${API}/api/marketplace/photo/${p}`),
            offers: {
              '@type': 'Offer',
              price: l.price,
              priceCurrency: 'USD',
              itemCondition: l.condition === 'new' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
              availability: l.status === 'active' ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
            },
          },
        });
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  const sendMessage = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate(`/login?redirect=/marketplace/listing/${id}`);
      return;
    }
    if (!message.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/marketplace/listings/${id}/contact`,
        { message: message.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Message sent to seller!');
      navigate(`/messages/${res.data.seller_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center"><div className="animate-spin w-10 h-10 border-4 border-[#D95800] border-t-transparent rounded-full" /></div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col items-center justify-center gap-4">
        <Package size={56} weight="duotone" className="text-neutral-300" />
        <p className="font-black uppercase text-neutral-700 dark:text-neutral-300">Listing not found</p>
        <Link to="/marketplace" className="text-sm font-bold text-[#D95800] underline underline-offset-4">Back to marketplace</Link>
      </div>
    );
  }

  const { listing: l, seller, similar } = data;
  const isOwner = !!localStorage.getItem('token') && seller && localStorage.getItem('userId') === seller.id;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-16" data-testid="marketplace-listing-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Link to="/marketplace" className="inline-flex items-center gap-1 text-sm font-bold text-neutral-500 hover:text-[#D95800] transition-colors" data-testid="listing-back-link">
          <CaretLeft size={14} weight="bold" /> Marketplace
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          {/* Photos */}
          <div>
            <div className="relative aspect-square bg-white dark:bg-neutral-800 border-2 border-black rounded-xl overflow-hidden shadow-[4px_4px_0px_#000]">
              {l.photos?.length > 0 ? (
                <img src={photoUrl(l.photos[activePhoto])} alt={l.title} className="w-full h-full object-contain" data-testid="listing-main-photo" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package size={80} weight="duotone" className="text-neutral-300 dark:text-neutral-600" />
                </div>
              )}
              {l.status === 'sold' && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="bg-red-600 text-white font-black uppercase text-2xl px-6 py-2 border-2 border-black -rotate-6">Sold</span>
                </div>
              )}
            </div>
            {l.photos?.length > 1 && (
              <div className="flex gap-2 mt-2">
                {l.photos.map((p, i) => (
                  <button key={p} onClick={() => setActivePhoto(i)} className={`w-16 h-16 border-2 rounded-lg overflow-hidden ${i === activePhoto ? 'border-[#D95800]' : 'border-black'}`}>
                    <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-start gap-2">
              <span className={`${CONDITION_COLORS[l.condition]} text-white text-[10px] font-black uppercase px-2 py-0.5 border border-black rounded`}>
                {CONDITION_LABELS[l.condition]}
              </span>
              <span className="bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 text-[10px] font-black uppercase px-2 py-0.5 border border-black rounded">
                {CATEGORY_LABELS[l.category]}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-neutral-900 dark:text-white mt-2" data-testid="listing-title">{l.title}</h1>
            <p className="text-3xl font-black text-[#D95800] mt-2" data-testid="listing-price">
              {formatPrice(l.price)}
              {l.negotiable && <span className="text-xs font-black uppercase text-neutral-500 ml-2">or best offer</span>}
            </p>

            <div className="flex flex-wrap gap-3 mt-3 text-sm text-neutral-600 dark:text-neutral-400">
              <span className="flex items-center gap-1"><MapPin size={15} weight="bold" /> {l.city}, {l.state}</span>
              {l.quantity > 1 && <span className="flex items-center gap-1"><Stack size={15} weight="bold" /> Qty: {l.quantity}</span>}
              <span className="flex items-center gap-1"><Eye size={15} weight="bold" /> {l.views || 0} views</span>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {(l.delivery_options || []).map((d) => (
                <span key={d} className="inline-flex items-center gap-1 bg-white dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 text-xs font-bold px-2.5 py-1 rounded-full">
                  {d === 'pickup' ? <HandArrowDown size={13} weight="bold" /> : <Truck size={13} weight="bold" />} {DELIVERY_LABELS[d]}
                </span>
              ))}
            </div>

            <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 mt-5">
              <p className="text-[10px] font-black uppercase tracking-widest text-neutral-400 mb-1.5">Description</p>
              <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed" data-testid="listing-description">{l.description}</p>
            </div>

            {/* Seller card */}
            {seller && (
              <div className="bg-[#0F172A] border-2 border-black rounded-xl p-4 mt-4 flex items-center gap-3" data-testid="listing-seller-card">
                <div className="w-11 h-11 rounded-full bg-[#2B4C6F] border-2 border-white/20 flex items-center justify-center overflow-hidden shrink-0">
                  {seller.profile_image
                    ? <img src={seller.profile_image} alt="" className="w-full h-full object-cover" />
                    : <span className="text-white font-black">{(seller.name || '?')[0]}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-white truncate">{seller.name}</p>
                  <p className="text-xs text-neutral-400 capitalize">
                    {seller.user_type}{seller.member_since ? ` · Member since ${seller.member_since}` : ''}
                  </p>
                  {seller.total_reviews > 0 && (
                    <p className="text-xs text-amber-400 flex items-center gap-1 mt-0.5">
                      <Star size={12} weight="fill" /> {seller.avg_rating} ({seller.total_reviews} reviews)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Contact / Buy */}
            {!isOwner && l.status === 'active' && (
              <>
                <button
                  onClick={async () => {
                    const token = localStorage.getItem('token');
                    if (!token) { navigate(`/login?redirect=/marketplace/listing/${id}`); return; }
                    setBuying(true);
                    try {
                      const res = await axios.post(
                        `${API}/api/marketplace/listings/${id}/checkout`,
                        { origin_url: window.location.origin, ...(acceptedOffer ? { offer_id: acceptedOffer._id } : {}) },
                        { headers: { Authorization: `Bearer ${token}` } },
                      );
                      openCheckout(res.data.checkout_url);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Could not start checkout');
                      setBuying(false);
                    }
                  }}
                  disabled={buying}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-black uppercase text-sm px-6 py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform disabled:opacity-50"
                  data-testid="listing-buy-now-btn"
                >
                  <CreditCard size={18} weight="fill" /> {buying ? 'Redirecting...' : acceptedOffer ? `Buy at Your Offer — ${formatPrice(acceptedOffer.amount)}` : `Buy Now — ${formatPrice(l.price)}`}
                </button>
                {acceptedOffer && (
                  <p className="text-[11px] font-bold text-emerald-600 text-center mt-1" data-testid="accepted-offer-note">
                    ✓ Seller accepted your {formatPrice(acceptedOffer.amount)} offer
                  </p>
                )}
                {myOffer?.status === 'countered' && (
                  <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500 rounded-xl p-3" data-testid="counter-offer-banner">
                    <p className="text-xs font-black uppercase text-amber-800 dark:text-amber-300">
                      🔁 Seller countered your {formatPrice(myOffer.amount)} offer at <span className="text-base">{formatPrice(myOffer.counter_amount)}</span>
                    </p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => respondCounter('accept')} className="flex-1 bg-emerald-600 text-white font-black uppercase text-xs py-2.5 border-2 border-black rounded-lg" data-testid="accept-counter-btn">
                        Accept — Buy at {formatPrice(myOffer.counter_amount)}
                      </button>
                      <button onClick={() => respondCounter('decline')} className="bg-white dark:bg-neutral-900 text-red-500 font-black uppercase text-xs px-3 py-2.5 border-2 border-black rounded-lg" data-testid="decline-counter-btn">
                        Decline
                      </button>
                    </div>
                  </div>
                )}
                {myOffer?.status === 'pending' && (
                  <p className="text-[11px] font-bold text-amber-600 text-center mt-2" data-testid="pending-offer-note">
                    ⏳ Your {formatPrice(myOffer.amount)} offer is pending — the seller will respond soon
                  </p>
                )}
                {!myOffer && (
                  showOffer ? (
                    <div className="mt-2 flex gap-2" data-testid="offer-form">
                      <input
                        type="number" min="1" step="1"
                        value={offerAmount}
                        onChange={(e) => setOfferAmount(e.target.value)}
                        placeholder={`Offer less than ${formatPrice(l.price)}`}
                        className="flex-1 text-sm px-3 py-2.5 border-2 border-black rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none"
                        data-testid="offer-amount-input"
                      />
                      <button onClick={sendOffer} disabled={!offerAmount} className="bg-emerald-600 text-white font-black uppercase text-xs px-4 border-2 border-black rounded-lg disabled:opacity-50" data-testid="offer-send-btn">Send</button>
                      <button onClick={() => setShowOffer(false)} className="text-xs font-bold text-neutral-500 px-1">✕</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowOffer(true)}
                      className="mt-2 w-full text-xs font-black uppercase text-emerald-700 dark:text-emerald-400 underline underline-offset-4"
                      data-testid="make-offer-btn"
                    >
                      💰 Make an offer
                    </button>
                  )
                )}
                <p className="text-[10px] text-neutral-400 text-center mt-1.5">Secure payment via Stripe · Buyer & seller connected after purchase</p>
              </>
            )}
            {!isOwner && l.status === 'active' && (
              showContact ? (
                <div className="mt-4 bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4" data-testid="listing-contact-form">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    placeholder={`Hi, is "${l.title}" still available?`}
                    className="w-full text-sm p-3 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:border-[#D95800]"
                    data-testid="listing-contact-message"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={sendMessage}
                      disabled={sending || !message.trim()}
                      className="flex-1 bg-[#D95800] text-white font-black uppercase text-sm py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] disabled:opacity-50"
                      data-testid="listing-contact-send"
                    >
                      {sending ? 'Sending...' : 'Send Message'}
                    </button>
                    <button onClick={() => setShowContact(false)} className="px-4 text-sm font-bold text-neutral-500">Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    if (!localStorage.getItem('token')) { navigate(`/login?redirect=/marketplace/listing/${id}`); return; }
                    setMessage(`Hi, is "${l.title}" still available?`);
                    setShowContact(true);
                  }}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform"
                  data-testid="listing-message-seller-btn"
                >
                  <ChatCircleDots size={18} weight="fill" /> Message Seller
                </button>
              )
            )}
            {isOwner && (
              <Link to="/marketplace/my-listings" className="mt-4 block text-center bg-[#2B4C6F] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000]" data-testid="listing-manage-link">
                Manage My Listings
              </Link>
            )}
          </div>
        </div>

        {/* Similar */}
        {similar?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
              <Tag size={18} weight="bold" className="text-[#D95800]" /> Similar items
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {similar.map((s) => <MarketplaceCard key={s._id} listing={s} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
